import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MdSearch, MdMoreVert, MdArchive, MdBlock, MdPerson, MdNotificationsOff } from 'react-icons/md';
import { CONNECTION_BADGE_COLORS } from './connectionBadge';
import { useIsMobile } from '../../hooks/useIsMobile';
import OptionsBottomSheet from './OptionsBottomSheet';

const MENU_Z_INDEX = 9999;

export default function ChatHeader({
  otherUserDisplayName,
  otherUserSecondary,
  otherUserPhotoUrl,
  connectionBadgeLabel,
  isOnline,
  lastSeen,
  onSearchInChat,
  onViewProfile,
  onMute,
  onArchive,
  onBlockOrReport,
  onClearChat,
  isMuted,
  onBack,
  showBackButton,
  t = (s) => s,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownRect, setDropdownRect] = useState(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!menuOpen) {
      setDropdownRect(null);
      return;
    }
    if (!isMobile && triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setDropdownRect({ top: r.bottom + 4, right: window.innerWidth - r.right, width: 208 });
    }
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [menuOpen, isMobile]);

  const sheetOptions = [
    { label: 'Pogledaj profil', icon: <MdPerson className="text-lg" />, onClick: () => onViewProfile?.() },
    { label: 'Arhiviraj', icon: <MdArchive className="text-lg" />, onClick: () => onArchive?.() },
    { label: 'Blokiraj / Prijavi', icon: <MdBlock className="text-lg" />, onClick: () => onBlockOrReport?.(), danger: true },
  ];
  if (onClearChat) sheetOptions.push({ label: 'Obriši razgovor', icon: <MdArchive className="text-lg" />, onClick: () => onClearChat() });

  const badgeClass = CONNECTION_BADGE_COLORS[connectionBadgeLabel] || CONNECTION_BADGE_COLORS['Ostalo'];

  return (
    <header className="flex items-center gap-3 px-4 py-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0">
      {showBackButton && (
        <button
          type="button"
          onClick={onBack}
          className="p-2 -ml-1 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500"
          aria-label="Nazad"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}
      <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center text-primary-600 dark:text-primary-400 font-semibold flex-shrink-0 overflow-hidden">
        {otherUserPhotoUrl ? (
          <img src={otherUserPhotoUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          (otherUserDisplayName || 'U')[0].toUpperCase()
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-base font-medium text-gray-900 dark:text-gray-100 truncate">
            {otherUserDisplayName || 'Korisnik'}
          </h1>
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${badgeClass}`}>
            {connectionBadgeLabel}
          </span>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {otherUserSecondary || (isOnline ? 'Na mreži' : lastSeen ? `Poslednja aktivnost ${lastSeen}` : '')}
        </p>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          type="button"
          onClick={onSearchInChat}
          className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
          aria-label="Pretraži u razgovoru"
        >
          <MdSearch className="text-xl" />
        </button>
        <button
          type="button"
          onClick={onMute}
          className={`p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            isMuted ? 'text-primary-600 dark:text-primary-400 bg-primary-100/50 dark:bg-primary-900/20' : 'text-gray-600 dark:text-gray-400'
          }`}
          aria-label={isMuted ? 'Ukloni utišavanje' : 'Utisaj'}
        >
          <MdNotificationsOff className="text-xl" />
        </button>
        <div className="relative" ref={menuRef}>
          <button
            ref={triggerRef}
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            aria-label="Više"
            aria-expanded={menuOpen}
          >
            <MdMoreVert className="text-xl" />
          </button>
          {isMobile && (
            <OptionsBottomSheet
              open={menuOpen}
              onClose={() => setMenuOpen(false)}
              options={sheetOptions}
              title="Opcije razgovora"
            />
          )}
          {!isMobile && menuOpen && dropdownRect && createPortal(
            <div
              className="fixed py-1 w-52 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-xl"
              style={{ top: dropdownRect.top, right: dropdownRect.right, zIndex: MENU_Z_INDEX }}
              onClick={(e) => e.stopPropagation()}
            >
              <button type="button" onClick={() => { onViewProfile?.(); setMenuOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                <MdPerson className="text-base" />Pogledaj profil
              </button>
              <button type="button" onClick={() => { onArchive?.(); setMenuOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                <MdArchive className="text-base" />Arhiviraj
              </button>
              <button type="button" onClick={() => { onBlockOrReport?.(); setMenuOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
                <MdBlock className="text-base" />Blokiraj / Prijavi
              </button>
              {onClearChat && (
                <button type="button" onClick={() => { onClearChat(); setMenuOpen(false); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                  Obriši razgovor
                </button>
              )}
            </div>,
            document.body
          )}
        </div>
      </div>
    </header>
  );
}
